import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, Check, Trash2, Package, Tag, AlertTriangle, Info } from 'lucide-react';
import { useSocket } from '../../../hooks/useSocket';
import { useAuth } from '../../../context/AuthContext';

const NotificationBell = () => {
  const { user } = useAuth();
  const { notifications, unreadCount, markNotificationsRead, isConnected } = useSocket(user?.id);
  const [isOpen, setIsOpen] = useState(false);
  const [localNotifications, setLocalNotifications] = useState([]);

  useEffect(() => {
    setLocalNotifications((prev) => {
      const merged = [...notifications, ...prev];
      return merged.filter((notif, index, self) => 
        index === self.findIndex((n) => n.id === notif.id)
      );
    });
  }, [notifications]);

  const getIcon = (type) => {
    switch (type) {
      case 'order': return Package;
      case 'promo': return Tag;
      case 'alert': return AlertTriangle;
      default: return Info;
    }
  };

  const getIconColor = (type) => {
    switch (type) {
      case 'success': return 'bg-green-100 text-green-600';
      case 'order': return 'bg-blue-100 text-blue-600';
      case 'promo': return 'bg-purple-100 text-purple-600';
      case 'alert': return 'bg-yellow-100 text-yellow-600';
      case 'error': return 'bg-red-100 text-red-600';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const handleOpen = () => {
    setIsOpen(!isOpen);
    if (!isOpen && unreadCount > 0) {
      markNotificationsRead();
    }
  };

  const clearAll = () => {
    setLocalNotifications([]);
  };

  const markAsRead = (id) => {
    setLocalNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  return (
    <div className="relative">
      <motion.button
        className="relative p-2.5 rounded-full hover:bg-gray-100 text-gray-600 transition-colors"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleOpen}
      >
        <Bell size={20} />

        <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full border-2 border-white ${
          isConnected ? 'bg-green-500' : 'bg-red-500'
        }`} />

        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              className="absolute -top-1 -right-1 bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold w-5 h-5 rounded-full flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0 }}
            >
              {unreadCount > 9 ? '9+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
            <motion.div
              className="absolute right-0 mt-3 w-96 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -10, scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900">Notifications</h3>
                  <p className="text-xs text-gray-500">
                    {isConnected ? (
                      <span className="flex items-center gap-1">
                        <span className="w-1.5 h-1.5 bg-green-500 rounded-full" />
                        Live updates active
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-red-500">
                        <span className="w-1.5 h-1.5 bg-red-500 rounded-full" />
                        Disconnected
                      </span>
                    )}
                  </p>
                </div>
                {localNotifications.length > 0 && (
                  <button
                    onClick={clearAll}
                    className="p-2 hover:bg-red-50 rounded-lg text-gray-400 hover:text-red-500 transition-colors"
                    title="Clear all"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>

              <div className="max-h-96 overflow-y-auto">
                {localNotifications.length === 0 ? (
                  <div className="p-8 text-center">
                    <Bell size={48} className="mx-auto text-gray-200 mb-3" />
                    <p className="text-gray-500 text-sm">No notifications yet</p>
                    <p className="text-gray-400 text-xs mt-1">You will see real-time updates here</p>
                  </div>
                ) : (
                  localNotifications.map((notification, index) => {
                    const Icon = getIcon(notification.type);
                    return (
                      <motion.div
                        key={notification.id}
                        className={`p-4 border-b border-gray-50 hover:bg-gray-50 transition-colors cursor-pointer ${
                          !notification.read ? 'bg-primary-50/30' : ''
                        }`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                        onClick={() => markAsRead(notification.id)}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${getIconColor(notification.type)}`}>
                            <Icon size={18} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="font-medium text-sm text-gray-900">{notification.title}</p>
                              {!notification.read && (
                                <span className="w-2 h-2 bg-primary-500 rounded-full flex-shrink-0" />
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {new Date(notification.timestamp).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })
                )}
              </div>

              {localNotifications.length > 0 && (
                <div className="p-3 border-t border-gray-100 text-center">
                  <button className="text-sm text-primary-600 font-medium hover:text-primary-700 transition-colors">
                    View all notifications
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationBell;
